import { useCallback, useEffect } from 'react';
import {
  useReportDefinition,
  useReportFiltersManagement,
  useReportSectionSync,
  useReportExport,
} from '@reports/presentation/hooks';
import { ReportTypes } from '@reports/domain/types';
import {
  ReportBreadcrumb,
  ReportFiltersBar,
  ReportHeader,
  ReportSection,
  ReportTabs,
} from '@reports/presentation/components/base';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ReportSection as ReportSectionType } from '@reports/domain/value-objects';

/**
 * Payments Report Content
 *
 * Main content component for the payments report.
 * Displays breadcrumb, header, filters bar, tabs, and sections.
 * Export toasts are handled automatically by the useReportExport hook.
 * Infrastructure dependencies are managed by composition root.
 */
export const PaymentsReportContent = () => {
  const { getReportDefinition } = useReportDefinition();
  const paymentsReport = getReportDefinition(ReportTypes.PAYMENTS);

  const { appliedFilters, updateFilters, selectReport } = useReportFiltersManagement({
    autoLoad: true,
    autoSave: true,
  });

  const { activeSectionKey, handleSectionChange } = useReportSectionSync({
    sections: paymentsReport.sections,
  });

  const { exportReport, isLoading } = useReportExport();

  useEffect(() => {
    selectReport(paymentsReport);
  }, []);

  const handleFiltersChange = useCallback(
    (filters: BlockDataRequestDto) => {
      updateFilters(filters);
    },
    [updateFilters],
  );

  const handleExport = useCallback(() => {
    exportReport(appliedFilters, paymentsReport.export);
  }, [appliedFilters, exportReport, paymentsReport.export]);

  return (
    <div className="w-full p-6 font-sans">
      <ReportBreadcrumb items={[{ label: paymentsReport.metadata.name }]} />

      <ReportHeader report={paymentsReport} />

      <ReportFiltersBar
        appliedFilters={appliedFilters}
        isExporting={isLoading}
        onExport={handleExport}
        onFiltersChange={handleFiltersChange}
        reportDefinition={paymentsReport}
      />

      <ReportTabs
        activeSectionKey={activeSectionKey}
        onSectionChange={handleSectionChange}
        sections={paymentsReport.sections}
      >
        {(section: ReportSectionType) => <ReportSection request={appliedFilters} section={section} />}
      </ReportTabs>
    </div>
  );
};
